import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, getApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentReturn() {
  const [search] = useSearchParams();
  const sessionId = search.get("session_id");
  const [tx, setTx] = useState(null);
  const [status, setStatus] = useState("checking"); // checking | paid | failed | expired

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let attempts = 0; let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setTx(data);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
        if (attempts >= 6) { setStatus("pending"); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        // eslint-disable-next-line no-console -- surface payment polling errors during debugging
        console.error("Payment status poll failed", getApiError(e));
        setStatus("failed");
      }
    };
    poll();
    return () => { cancelled = true; };
    // Intentionally one-shot per sessionId; local `attempts` and `cancelled`
    // are closure state for this single poll lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-paper" data-testid="payment-return-page">
      <Navbar />
      <main className="max-w-xl mx-auto px-4 py-16">
        <Card className="border-black/5">
          <CardContent className="p-10 text-center">
            {status === "checking" && (
              <>
                <Loader2 className="h-10 w-10 mx-auto animate-spin text-[color:var(--orange)]" />
                <h1 className="font-serif text-3xl mt-4">Confirming payment…</h1>
              </>
            )}
            {status === "paid" && (
              <>
                <CheckCircle2 className="h-12 w-12 mx-auto text-[color:var(--bright-green)]" />
                <h1 className="font-serif text-3xl mt-4">Payment received</h1>
                <p className="text-[color:var(--text-secondary)] mt-2">
                  ${tx?.amount?.toLocaleString()} · {tx?.description}
                </p>
                <Button asChild className="mt-6 bg-[color:var(--fire-red)] hover:bg-[color:var(--fire-red-hover)] text-white" data-testid="payment-portal-btn">
                  <Link to="/portal">Go to portal</Link>
                </Button>
              </>
            )}
            {(status === "failed" || status === "expired") && (
              <>
                <XCircle className="h-12 w-12 mx-auto text-[color:var(--fire-red)]" />
                <h1 className="font-serif text-3xl mt-4">{status === "expired" ? "Session expired" : "Payment not confirmed"}</h1>
                <Button asChild variant="outline" className="mt-6 border-black/10">
                  <Link to="/pricing">Back to pricing</Link>
                </Button>
              </>
            )}
            {status === "pending" && (
              <>
                <Loader2 className="h-10 w-10 mx-auto animate-spin text-[color:var(--orange)]" />
                <h1 className="font-serif text-3xl mt-4">Still processing…</h1>
                <p className="text-[color:var(--text-secondary)] mt-2 text-sm">Check the portal in a minute — we'll mark it active automatically.</p>
                <Button asChild className="mt-6" data-testid="payment-portal-btn">
                  <Link to="/portal">Go to portal</Link>
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
}
